import { first, forEach, get, groupBy, keys, map, set, uniq } from 'es-toolkit/compat';
import { OrchestrationByStainByResultName } from 'utils/orchestrationSelections';

export interface OrchestrationParamsDeps {
  classes?: string[];
  modelTsm?: string;
  dynamicCellDetectionConfig?: string;
  useDynamicCellDetection?: boolean;
  dedupValue?: number;
  detectionResults?: string;
  modelMplexBinaryClassification?: string;
  flowVersion?: string;
}

export enum OrchestrationDataType {
  Categorical = 'categorical',
  CategoricalArray = 'categorical_array',
  Int = 'int',
  Float = 'float',
  Boolean = 'boolean',
  Point = 'point',
  Multipolygon = 'multipolygon',
  BooleanArray = 'boolean_array',
}

export const orchestrationDataTypeNumbers = [OrchestrationDataType.Float, OrchestrationDataType.Int];

export interface NumberOption {
  min?: number;
  max?: number;
}

export interface OrchestrationData {
  label?: string;
  type?: OrchestrationDataType;
  options?:
    | string[] // Categorical
    | NumberOption; // Float, Int
}

export interface OrchestrationParams {
  resultType?: string;
  studyId?: string;
  orchestrationId: string;
  flowClassName?: string;
  deps: OrchestrationParamsDeps;
  expirationDate?: string;
  gitHash?: string;
  data?: OrchestrationData[];
}

export interface OrchestrationInference {
  orchestrationId: string;
  slideIds: string[];
  createdAt: Date;
  tsmSlideOverrides: { [slideId: string]: string };
  params: OrchestrationParams;
  orchestrationResultArtifactUrlPattern: string;
  orchestrationTsmArtifactUrlPattern?: string;
}

export interface InferenceModelData {
  modelUrl: string;
  modelId?: string;
  modelType: string;
  classes?: string[];
}

export interface ModelInference extends InferenceModelData {
  orchestrations?: OrchestrationInference[];
}

export const DEFAULT_RESULT_NAME = '__all__';

export interface ResultNameOrchestrations {
  resultName: string;
  resultType: string;
  orchestrations: OrchestrationInference[];
}

export interface SlideInferenceResults {
  slideId: string;
  stainingType: string;
  originalFileName: string;
  caseId: string;
  caseLabel: string;
  negativeControl: boolean;
}

export interface SlideInferenceResultsPage {
  items: SlideInferenceResults[];
  nextCursor?: string | null;
}

export interface MapValuesToBins {
  bin: string | number;
  minValue?: number;
  maxValue?: number;
}

export interface ClassificationBinningParams {
  numBins?: number;
  mapValuesToBins?: MapValuesToBins[];
  // Scopes this binning definition to specific generated columns (e.g. one RNA probe's count
  // column), so a single Input can carry different bin thresholds per probe.
  columnsToBin?: string[];
}

export interface SpotDetectionCountBin {
  minCount?: number;
  maxCount?: number;
}

export interface Input {
  modelType?: string;
  flowClassName?: string;
  artifactUrl?: string;
  assignmentId?: number;
  classesToUse?: string[];
  classesToIgnore?: string[];
  readAs?: string;
  joinMode?: string;
  binning?: ClassificationBinningParams | ClassificationBinningParams[];
  transferAreasToRegisteredStains?: boolean;
  segmentationCompartments?: string[];
  segmentationDilationsUm?: number[];
  countBins?: SpotDetectionCountBin[];
}

export interface ResultsInput {
  input: Input;
  override?: Input;
}

export interface DefectModel {
  path: string;
  excludeClasses: string[];
}

export interface Inputs {
  // caseId
  [key: string]: {
    // slideId
    [key: string]: ResultsInput[];
  };
}

export interface PipsExpectedResults {
  expectedFeatures: string[];
  expectedResults: OrchestrationData[];
}

export const getStainOfOrchestration = (slidesById: Record<string, SlideInferenceResults>, slide_ids: string[]) =>
  slidesById[first(slide_ids)]?.stainingType;

export const getOrchestrationKey = (resultNameOrchestrations: Partial<ResultNameOrchestrations>) => {
  const resultName = get(resultNameOrchestrations, 'resultName');
  const resultType = get(resultNameOrchestrations, 'resultType');

  if (resultName === DEFAULT_RESULT_NAME && resultType) {
    return resultType;
  }
  return resultName;
};

/**
 * Helper to organize artifacts by stain type and result name.
 *
 * This function transforms result orchestrations into a structure grouped by
 * stain type and result name. Useful for organizing post-processing results,
 * quantification results, and other artifact types.
 *
 * @param slidesById - Slides indexed by slideId for stain lookup
 * @param artifacts - Array of result orchestrations to organize
 * @returns Organized artifacts structure grouped by stain and result name
 */
export const getResultsByStainByResultName = (
  slidesById: Record<string, SlideInferenceResults>,
  orchestrationsByResultName: ResultNameOrchestrations[]
) => {
  const resultsByStainsByResultName: OrchestrationByStainByResultName = {};

  forEach(orchestrationsByResultName, (resultNameOrchestrations) => {
    const resultKey = getOrchestrationKey({
      resultName: resultNameOrchestrations?.resultName,
      resultType: resultNameOrchestrations?.resultType,
    });
    forEach(resultNameOrchestrations?.orchestrations, (orchestration) => {
      const stain = getStainOfOrchestration(slidesById, orchestration.slideIds);

      // Initialize nested structure if not already present
      set(resultsByStainsByResultName, [stain], get(resultsByStainsByResultName, [stain], {}));
      set(resultsByStainsByResultName, [stain, resultKey], get(resultsByStainsByResultName, [stain, resultKey], []));

      resultsByStainsByResultName[stain][resultKey].push(orchestration);
    });
  });

  return resultsByStainsByResultName;
};

export const getModelsTypeByModelInferences = (inferenceArtifacts: InferenceModelData[]): string[] =>
  keys(groupBy(inferenceArtifacts, 'modelType'));

export const getResultNameTypesByOrchestrations = (
  orchestrationsArtifactsByResultName: ResultNameOrchestrations[]
): string[] => uniq(map(orchestrationsArtifactsByResultName, getOrchestrationKey));
